/**
 * Error handling utilities for consistent error processing
 */

import error from '../config/error.json';
import { logError } from './log';

/**
 * Known safe error message patterns that can be shown to users.
 * These come from our own contract layer (fundraisin.js) and wallet code.
 * Any message NOT matching these patterns will be replaced with a generic error.
 */
const SAFE_ERROR_PATTERNS = [
  'wallet connection',
  'connect your wallet',
  'wallet not connected',
  'transaction rejected by user',
  'transaction failed',
  'no wallet found',
  'please install',
  'network not supported',
  'connection rejected',
  'minting has ended',
  'not for sale',
  'not owned by you',
  'already owned by you',
  'does not exist',
  'not enough to buy',
  'contract initialization failed',
  'please connect your wallet',
];

/**
 * Check if an error message is safe to display to users
 * @param {string} message - Error message to check
 * @returns {boolean} - True if safe to display
 */
const isSafeErrorMessage = (message) => {
  if (!message) return false;
  const lowerMessage = message.toLowerCase();
  return SAFE_ERROR_PATTERNS.some(pattern => lowerMessage.includes(pattern));
};

/**
 * Parse and format error messages for user display
 * @param {Error} err - Error object
 * @param {Object} themeConfig - Theme configuration
 * @param {string} action - Action being performed (e.g., 'buy', 'mint')
 * @returns {string} - Formatted error message
 */
export const parseErrorMessage = (err, themeConfig, action = 'transaction') => {
  logError(err);
  
  if (!err) return error.UNKNOWN_ERROR;

  // Normalize error structure
  if (err.error && err.error.data && !err.data) {
    err.data = err.error.data;
  }

  let errorMsg = '';
  // Handle specific error types
  if (err.data && err.data.message) {
    const customMessage = err.data.message.split(':');
    
    if (customMessage.length > 1 && error[customMessage[1].trim()]) {
      errorMsg = error[customMessage[1].trim()]
        .replace('%1', themeConfig?.APP_NFT_NAME || 'NFT')
        .replace('%2', action);
    } else if ([32000].includes(Math.abs(err.data.code))) {
      errorMsg = error[Math.abs(err.data.code)]
        ?.replace('%1', themeConfig?.APP_NFT_NAME || 'NFT')
        ?.replace('%2', action) || error.UNKNOWN_ERROR;
    } else {
      // Don't expose raw contract/server messages to users
      logError('Unhandled data error:', err.data.message);
      errorMsg = error.UNKNOWN_ERROR;
    }
  } else if ([4001, 4100].includes(Math.abs(err.code))) {
    // User rejection errors
    errorMsg = error[Math.abs(err.code)]
      ?.replace('%1', themeConfig?.APP_NFT_NAME || 'NFT')
      ?.replace('%2', action) || 'Transaction was cancelled by user';
  } else if (['ACTION_REJECTED'].includes(err.code)) {
    errorMsg = error['4001']
  } else if (err.code === 'INSUFFICIENT_FUNDS' || (err.message && err.message.toLowerCase().includes('insufficient funds'))) {
    errorMsg = `Insufficient funds in your wallet to ${action} this ${themeConfig?.APP_NFT_NAME || 'NFT'}. Please add more funds and try again.`;
  } else if (err.message && isSafeErrorMessage(err.message)) {
    // Known safe user-facing messages from contract layer
    errorMsg = err.message;
  } else {
    // Don't expose raw error messages (may contain sensitive info like contract addresses, methods, etc.)
    logError('Unhandled error:', err.message || err);
    errorMsg = error.UNKNOWN_ERROR;
  }

  return errorMsg;
};

/**
 * Create a user-friendly error message
 * @param {string} code - Error code
 * @param {Object} context - Additional context for the error
 * @returns {string} - User-friendly error message
 */
export const createUserFriendlyError = (code, context = {}) => {
  const errorMapping = {
    WALLET_NOT_CONNECTED: 'Please connect your wallet to continue',
    INSUFFICIENT_FUNDS: 'Insufficient funds in your wallet',
    TRANSACTION_FAILED: 'Transaction failed. Please try again',
    NETWORK_ERROR: 'Network error. Please check your connection',
    CONTRACT_ERROR: 'Smart contract error. Please contact support',
  };

  return errorMapping[code] || `Error: ${code}`;
};

/**
 * Check if error is a user cancellation
 * @param {Error} err - Error object
 * @returns {boolean} - True if user cancelled
 */
export const isUserCancellation = (err) => {
  const cancellationCodes = [4001, 4100];
  return cancellationCodes.includes(Math.abs(err.code));
};
